// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { selectPricingInfos } from '@gandi/billing';
import {
  createCart,
  getCart,
  selectCartUUID,
  selectCartUUID as selectCartId,
  selectCartIsActive,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useResourcePost } from '@gandi/resource/react';
import { useTranslator } from '@gandi/translate';

import { Base } from '@gandi/ui';

import { normalize } from 'src/Domain/BulkInput';
import DomainPageTitle from 'src/components/DomainPageTitle';
import { Layout } from 'src/components/Layout';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { bulkResource } from '../../DomainTransferBulkResult/modules/cart';
import { validateSearch as submitSearch } from '../modules/actions';

import DomainInput from './DomainInput';

import { DomainList } from './DomainList';
import { Loading } from './Loading';
import { TransferCommercialBlock } from './TransferCommercialBlock';

const inputId = 'search';

/**
 * [\uFEFF]?       => optional BOM
 * ['"]?           => optional starting quote
 * fqdn            => literal 'fqdn'
 * ['"]?           => optional ending quote
 * \s*             => optional spaces
 * [;,]            => separator (semicolon or comma)
 * .*              => anything after
 */
const headerRegex = /^[\uFEFF]?['"]?fqdn['"]?\s*[;,].*/;
/** Add CSV header if not present */
function addHeaderToCSV(existingContent: string): string {
  if (headerRegex.test(existingContent.trim().split('\n')[0])) {
    return existingContent;
  }
  const header = 'fqdn;authcode';
  return `${header}\n${existingContent}`;
}

export function TransferPage(): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);

  const [searchParams] = useSearchParams();
  const displayBulk = searchParams.has('bulk');

  const cartExists = useAppSelector(
    state => selectCartId(state) && selectCartIsActive(state),
  );
  const cartUUID = useAppSelector(state => cartExists && selectCartUUID(state));
  const { currency, country } = useAppSelector(selectPricingInfos);

  React.useEffect(() => {
    if (displayBulk && !cartUUID) {
      dispatch(createCart({ currency, country }));
    }
  }, [displayBulk, cartUUID, country, currency, dispatch]);

  const title = <DomainPageTitle process="transfer" />;

  const { handlePost } = useResourcePost(
    bulkResource,
    {
      cart_uuid: cartUUID,
    },
    { headers: { 'Content-Type': 'text/csv; charset=utf-8' } },
  );

  function handleSearch(value: string) {
    if (displayBulk) {
      setIsLoading(true);
      const csv = addHeaderToCSV(normalize(value, '\n'));
      handlePost(csv)
        .then(() => {
          dispatch(getCart());
          navigate('/domain/transfer/bulk-result');
        })
        .catch(err => {
          dispatch(
            notifyDanger({
              message:
                err._error ||
                err.response.data.error || // Non-standard 400 error from the API
                __('An unknown error occurred. Please try again later.'),
            }),
          );
          setIsLoading(false);
        });
    } else {
      dispatch(submitSearch(value));
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <Base marginBottom="base">
        <DomainInput
          id={inputId}
          process="transfer"
          title={title}
          label={__('Transfer a domain name')}
          submitSearch={handleSearch}
        />
      </Base>
      {!displayBulk && <DomainList />}
      <TransferCommercialBlock />
    </Layout>
  );
}
